import { _assert, isServerSide, loadScript, UnixTimestampMillis } from '@naturalcycles/js-lib'
import { mixpanelService } from './mixpanel.service'

export enum MoeEventName {
  MOE_ONSITE_MESSAGE_SHOWN = 'MOE_ONSITE_MESSAGE_SHOWN',
  MOE_ONSITE_MESSAGE_CLICKED = 'MOE_ONSITE_MESSAGE_CLICKED',
  MOE_ONSITE_MESSAGE_DISMISSED = 'MOE_ONSITE_MESSAGE_DISMISSED',
  MOE_ONSITE_MESSAGE_AUTO_DISMISS = 'MOE_ONSITE_MESSAGE_AUTO_DISMISS',
}

export const MoeEventKey = 'MOE_AUTOMATED_EVENTS'

/**
 * Handles Moengage events and sends them to Mixpanel
 */
class MoeService {
  private inited = false
  async init(): Promise<void> {
    if (
      (isServerSide() && process.env['GATSBY_ENV'] !== 'test') ||
      this.inited ||
      !window.Cookiebot?.consent?.statistics
    ) {
      return
    }

    // Let's set before any async code to avoid concurrent inits
    this.inited = true

    await this.loadMoe()

    this.registerListeners()
  }

  /**
   * https://developers.moengage.com/hc/en-us/articles/360061573232-Configure-and-Integrate-On-site-Messaging#h_01H9JF27NE0Q2K7PRWWGQWVC5Q
   */
  registerListeners(): void {
    globalThis.addEventListener(MoeEventKey, (event: CustomEvent<MoeWindowEvent>) => {
      if (!this.isMoeEvent(event)) {
        return
      }
      const dataAsObj = Object.fromEntries(
        event.detail.data.map(v => [v.key.replace('moe_', ''), v.value]),
      )
      mixpanelService.track(event.detail.name, dataAsObj)
    })
  }

  isMoeEvent(event: Event): event is CustomEvent<MoeWindowEvent> {
    return !!MoeEventName[(event as CustomEvent<MoeWindowEvent>).detail?.name]
  }

  async loadMoe(): Promise<void> {
    const GATSBY_MOENGAGE_APP_ID = process.env['GATSBY_MOENGAGE_APP_ID']
    _assert(GATSBY_MOENGAGE_APP_ID, 'GATSBY_MOENGAGE_APP_ID is required')

    const moeDataCenter = 'dc_2' // Not expected to change, but keeping variable for clarity

    const webSdkUrl = `https://cdn.moengage.com/release/${moeDataCenter}/moe_webSdk.min.latest.js`
    await loadScript(webSdkUrl)

    globalThis.Moengage = globalThis.moe?.({
      app_id: GATSBY_MOENGAGE_APP_ID,
      debug_logs: Number(false),
      disable_web_push: true,
      enableSPA: true,
    })
  }
}

export const moeService = new MoeService()

interface MoeWindowEventData<K, V> {
  key: K
  value: V
  updated_at: UnixTimestampMillis
}
export interface MoeWindowEvent {
  name: MoeEventName
  data: (
    | MoeWindowEventData<
        | 'campaign_id'
        | 'campaign_name'
        | 'type'
        | 'cid'
        | 'moe_locale_name'
        | 'moe_locale_id'
        | 'moe_variation_id'
        | 'URL',
        string
      >
    | MoeWindowEventData<'moe_logged_in_status' | 'moe_first_visit', boolean>
  )[]
}

export interface MoeInitParams {
  app_id: string
  debug_logs: number
  disable_web_push: boolean
  enableSPA: boolean
}

/**
 * The object generated by the MoEngage SDK initialization.
 * It has more properties, but we only care about this one for now.
 */
export interface Moe {
  initialised: number
  invoked: number
}
